import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { i18nLanguage } from "i18n";
import { useTripperTrack } from "../context/TripperTrackingContext";
import { resolveTripperTextButtonLabel } from "../tripperTextButtonLabel";
import { TripperModal } from "./TripperModal";

type TripperTextButtonProps = {
  itineraryId: string;
  language: i18nLanguage;
  label?: string;
};

export const TripperTextButton = ({
  itineraryId,
  language,
  label,
}: TripperTextButtonProps) => {
  const { t, i18n } = useTranslation("tripper", { keyPrefix: "EMBED" });
  const [modalOpen, setModalOpen] = useState(false);
  const trackPostHogEvent = useTripperTrack();
  const buttonRef = useRef<HTMLButtonElement>(null);
  // Flip when tripper.json lands. The provider also waits on widgets.json,
  // which is much larger and is not needed for this label.
  const [tripperLocaleReady, setTripperLocaleReady] = useState(() =>
    i18n.hasResourceBundle(language, "tripper"),
  );

  useEffect(() => {
    if (i18n.hasResourceBundle(language, "tripper")) {
      setTripperLocaleReady(true);
      return;
    }
    setTripperLocaleReady(false);
    const onAdded = (lng: string, ns: string) => {
      if (ns === "tripper" && lng === language) setTripperLocaleReady(true);
    };
    i18n.store.on("added", onAdded);
    return () => {
      i18n.store.off("added", onAdded);
    };
  }, [i18n, language]);

  const buttonLabel = resolveTripperTextButtonLabel(
    label,
    tripperLocaleReady ? t("TEXT_BUTTON_DEFAULT", { lng: language }) : "",
    language,
  );

  // Fire widget_impression once when the button enters the viewport
  useEffect(() => {
    const el = buttonRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          trackPostHogEvent("widget_impression");
          observer.disconnect();
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [trackPostHogEvent]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="tripper-text-button"
        onClick={() => {
          trackPostHogEvent("widget_launch");
          setModalOpen(true);
        }}
      >
        <span className="tripper-text-button__icon">
          <MapPinIcon />
        </span>
        {buttonLabel}
      </button>

      {modalOpen && (
        <TripperModal
          itineraryId={itineraryId}
          language={language}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  );
};

const MapPinIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);
